import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Loader2, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import { ACTIONS, getStatusLabel, getStatusValue } from "../constants/actions";

const ROW_HEIGHT = 76;
const DROPDOWN_GAP = 4;
const MIN_WIDTH = 220;

const getStatusId = (status) => {
  if (!status || typeof status === "string") return "";
  return status.id || status.status_id || "";
};

const getStatusKey = (status) => getStatusValue(status).toLowerCase();

const getActionMeta = (status) => {
  const statusKey = getStatusKey(status);
  return ACTIONS.find((a) => a.status === statusKey) || null;
};

const StatusBarDropdown = ({
  currentStatus,
  updateStatus,
  loading,
  disabled,
  allowedStatus = [],
  onUndo,
  canUndo = false,
  undoLabel = "Undo last status change",
}) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  const triggerRef = useRef(null);
  const listRef = useRef(null);

  const isLocked = !!loading || !!disabled;
  const showUndoRow = canUndo && !isLocked;

  const currentStatusId = getStatusId(currentStatus);
  const normalizedCurrentStatus = getStatusKey(currentStatus);

  const normalizedAllowedStatus = useMemo(() => {
    if (!Array.isArray(allowedStatus)) return [];

    return allowedStatus.filter((item) => {
      const itemId = getStatusId(item);
      const itemKey = getStatusKey(item);

      if (currentStatusId && itemId) {
        return itemId !== currentStatusId;
      }

      return itemKey !== normalizedCurrentStatus;
    });
  }, [allowedStatus, currentStatusId, normalizedCurrentStatus]);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // The undo row is a row: a Hired or Rejected candidate has no forward moves,
    // and counting only those sized the menu at a few pixels and clipped it.
    const filteredCount = normalizedAllowedStatus.length + (showUndoRow ? 1 : 0);
    const estimatedHeight = filteredCount * ROW_HEIGHT;
    const spaceBelow = viewportHeight - rect.bottom - DROPDOWN_GAP;
    const spaceAbove = rect.top - DROPDOWN_GAP;
    const openBelow = spaceBelow >= estimatedHeight || spaceBelow >= spaceAbove;
    const minWidth = Math.max(rect.width, MIN_WIDTH);

    // Anchored by its right edge, which is the trigger's, and left to size to its
    // content. Pinned to a fixed width the menu clipped any label longer than it
    // (the undo row does not wrap), so it grows instead, up to the room between
    // the trigger's right edge and the viewport's left.
    const right = Math.max(8, viewportWidth - rect.right);
    const maxWidth = Math.max(minWidth, viewportWidth - right - 8);

    const style = {
      position: "fixed",
      right,
      width: "max-content",
      minWidth,
      maxWidth,
      zIndex: 9999,
      maxHeight: Math.min(
        estimatedHeight + 8,
        openBelow ? spaceBelow : spaceAbove
      ),
    };

    if (openBelow) {
      style.top = rect.bottom + DROPDOWN_GAP;
    } else {
      style.bottom = viewportHeight - rect.top + DROPDOWN_GAP;
    }

    setDropdownStyle(style);
  }, [normalizedAllowedStatus.length, showUndoRow]);

  useEffect(() => {
    if (open) calculatePosition();
  }, [open, calculatePosition]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("resize", calculatePosition);
    return () => window.removeEventListener("resize", calculatePosition);
  }, [open, calculatePosition]);

  useEffect(() => {
    const handler = (e) => {
      if (
        !triggerRef.current?.contains(e.target) &&
        !listRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!open) return;

    const handler = (e) => {
      if (!listRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };

    window.addEventListener("scroll", handler, true);
    return () => window.removeEventListener("scroll", handler, true);
  }, [open]);

  const handleSelect = (statusItem) => {
    if (isLocked) return;

    setOpen(false);

    updateStatus({
      id: getStatusId(statusItem),
      code: statusItem?.code || "",
      name: getStatusLabel(statusItem),
      raw: statusItem,
    });
  };

  const displayStatus = getStatusLabel(currentStatus);
  const currentAction = getActionMeta(currentStatus);
  const hasTransitions = normalizedAllowedStatus.length > 0;

  return (
    <div className="relative">
      {allowedStatus === null ? (
        <div className="h-[36px] w-[120px] animate-pulse rounded-full bg-background-tertiary" />
      ) : !hasTransitions && !canUndo ? (
        <span
          className={cn(
            "flex items-center gap-1.5 rounded-full bg-button-secondary px-4 py-2 text-sm font-medium text-text-base",
            "select-none cursor-default"
          )}
        >
          {currentAction?.icon && (
            <currentAction.icon
              size={14}
              style={{ color: currentAction.iconColor }}
              aria-hidden="true"
            />
          )}
          <span>{displayStatus}</span>
        </span>
      ) : (
        <>
          <Button
            ref={triggerRef}
            type="button"
            onClick={() => !isLocked && setOpen((prev) => !prev)}
            className={cn(
              "flex items-center gap-1.5 rounded-full bg-button-secondary px-4 py-2 text-sm font-medium text-white",
              "select-none transition-all duration-100 focus:outline-none",
              isLocked ? "cursor-not-allowed opacity-50" : "cursor-pointer"
            )}
          >
            {currentAction?.icon && (
              <currentAction.icon
                size={14}
                style={{ color: currentAction.iconColor }}
                aria-hidden="true"
              />
            )}

            <span>{displayStatus}</span>

            <ChevronDown
              size={14}
              className={cn(
                "shrink-0 transition-transform duration-200",
                open && "rotate-180"
              )}
              aria-hidden="true"
            />
          </Button>

          {open &&
            createPortal(
              <div
                ref={listRef}
                style={dropdownStyle}
                className="overflow-y-auto overflow-x-hidden rounded-lg border border-border-primary bg-background-secondary shadow-lg custom-scrollbar"
              >
                {showUndoRow && (
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setOpen(false);
                        onUndo?.();
                      }}
                      disabled={!!loading}
                      className="flex w-full items-center gap-2.5 whitespace-nowrap rounded-none px-4 py-2 text-left text-sm font-medium text-text-primary hover:bg-warning/10 focus:outline-none disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <RotateCcw size={13} className="shrink-0 text-warning" />
                      <span>{undoLabel}</span>
                    </Button>
                    {hasTransitions && <hr className="my-1 border-border-primary" />}
                  </>
                )}
                {normalizedAllowedStatus.map((statusItem, index) => {
                  const action = getActionMeta(statusItem);
                  const Icon = action?.icon;
                  const statusId = getStatusId(statusItem);
                  const isLoadingThis =
                    typeof loading === "string" && loading === statusId;

                  return (
                    <Button
                      key={
                        statusItem?.id ||
                        statusItem?.status_id ||
                        statusItem?.code ||
                        statusItem?.name ||
                        index
                      }
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSelect(statusItem)}
                      onMouseEnter={() => setHovered(index)}
                      onMouseLeave={() => setHovered(null)}
                      disabled={!!loading}
                      className={cn(
                        "flex w-full items-center gap-2.5 rounded-none px-4 py-2 text-left text-sm font-medium",
                        "text-text-primary transition-colors focus:outline-none",
                        "disabled:cursor-not-allowed disabled:opacity-40",
                        hovered === index
                          ? "bg-background-tertiary"
                          : "bg-transparent"
                      )}
                    >
                      <span className="flex shrink-0 items-center justify-center text-text-primary">
                        {isLoadingThis ? (
                          <Loader2 size={13} className="animate-spin" />
                        ) : Icon ? (
                          <Icon size={13} aria-hidden="true" />
                        ) : null}
                      </span>

                      <span className="truncate">{getStatusLabel(statusItem)}</span>
                    </Button>
                  );
                })}
              </div>,
              document.body
            )}
        </>
      )}
    </div>
  );
};

export default StatusBarDropdown;